import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2, Save, Play, History } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Spinner, EmptyState, Badge, Alert } from '@/components/ui/misc';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: Boolean(projectId),
  });
  const runsQuery = useQuery({
    queryKey: ['project-runs', projectId],
    queryFn: () => api.listProjectRuns(projectId!),
    enabled: Boolean(projectId),
  });

  const [name, setName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [competitors, setCompetitors] = useState('');

  useEffect(() => {
    const p = projectQuery.data?.project;
    if (p) {
      setName(p.name);
      setBrandName(p.brandName ?? '');
      setDescription(p.description ?? '');
      setCompetitors((p.competitors ?? []).join(', '));
    }
  }, [projectQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.updateProject(projectId!, {
        name,
        brandName: brandName || null,
        brandNameSource: brandName ? 'user' : 'unknown',
        description,
        competitors: competitors
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.deleteProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/app/projects');
    },
  });

  async function rerun() {
    const p = projectQuery.data?.project;
    if (!p) return;
    setError(null);
    setRunning(true);
    try {
      const res = await api.createAnalysis({
        websiteUrl: p.websiteUrl,
        brandName: p.brandName,
        industry: p.industry,
        competitors: p.competitors,
        market: p.market,
        language: p.language,
        projectId: p.id,
      });
      navigate(`/app/results/${res.run.runId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not run analysis.');
    } finally {
      setRunning(false);
    }
  }

  if (projectQuery.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (projectQuery.error || !projectQuery.data) {
    return (
      <EmptyState
        title="Project not found"
        action={
          <Link to="/app/projects">
            <Button>Back to projects</Button>
          </Link>
        }
      />
    );
  }

  const project = projectQuery.data.project;
  const runs = runsQuery.data?.runs ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/app/projects"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={15} /> Back to projects
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">{project.name}</h1>
          <div className="flex gap-2">
            <Button onClick={rerun} disabled={running}>
              {running ? <Spinner /> : <Play size={16} />}
              Run analysis
            </Button>
          </div>
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Settings */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Project details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="d-name">Project name</Label>
                <Input id="d-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="d-brand">Brand name</Label>
                <Input id="d-brand" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Website</Label>
              <div className="input-base text-text-secondary">{project.websiteUrl}</div>
            </div>
            <div>
              <Label htmlFor="d-desc">Description</Label>
              <Textarea
                id="d-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="d-comp">Competitors (comma-separated)</Label>
              <Input
                id="d-comp"
                value={competitors}
                onChange={(e) => setCompetitors(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : <Save size={16} />}
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Meta */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row label="Industry" value={project.industry || '—'} />
              <Row label="Market" value={project.market} />
              <Row label="Language" value={project.language} />
              <Row label="Brand source" value={project.brandNameSource} />
              <Row label="Created" value={formatDate(project.createdAt)} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <Button
                variant="danger"
                className="w-full"
                onClick={() => {
                  if (confirm('Delete this project and its history? This cannot be undone.'))
                    deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
              >
                <Trash2 size={16} /> Delete project
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Runs */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis history</CardTitle>
          <Badge tone="default">{runs.length} runs</Badge>
        </CardHeader>
        <CardContent className="px-0">
          {runsQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : runs.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-text-muted">
              No analyses yet. Run one to see results here.
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {runs
                .slice()
                .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                .map((run) => (
                  <li key={run.runId}>
                    <Link
                      to={`/app/results/${run.runId}`}
                      className="flex items-center justify-between px-5 py-3 transition hover:bg-bg-raised/40"
                    >
                      <div className="flex items-center gap-3">
                        <History size={16} className="text-text-muted" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            Visibility {run.metrics?.visibilityScore.toFixed(1) ?? '—'}
                          </p>
                          <p className="text-xs text-text-muted">{formatDate(run.createdAt)}</p>
                        </div>
                      </div>
                      <Badge tone={run.status === 'completed' ? 'teal' : 'blue'}>{run.status}</Badge>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className="font-medium text-text-primary">{value}</span>
    </div>
  );
}
